import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./Button.scss";

/**
 * Button primitive.
 * - `to`   renders a router <Link>
 * - `href` renders an <a> (external links open in a new tab)
 * - otherwise renders a <button>
 */
const Button = ({
  to,
  href,
  variant = "primary",
  size = "md",
  icon: Icon,
  iconPosition = "end",
  external,
  className = "",
  children,
  ...rest
}) => {
  const classes = `btn btn--${variant} btn--${size} ${className}`.trim();
  const content = (
    <>
      {Icon && iconPosition === "start" && <Icon className="btn__icon" aria-hidden="true" />}
      <span>{children}</span>
      {Icon && iconPosition === "end" && <Icon className="btn__icon" aria-hidden="true" />}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  if (href) {
    const isExternal = external ?? /^https?:\/\//.test(href);
    return (
      <a
        href={href}
        className={classes}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  );
};

Button.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  variant: PropTypes.oneOf(["primary", "secondary", "ghost"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  icon: PropTypes.elementType,
  iconPosition: PropTypes.oneOf(["start", "end"]),
  external: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Button;
